import {
  CustomTheme,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import React, { useMemo } from 'react';

import {
  SIGNATURE_COMPONENT_DEFAULT_HEIGHT,
  SIGNATURE_COMPONENT_DEFAULT_WIDTH,
} from 'src/constants';
import { useAppSelector } from 'src/hooks/useStore';
import { signaturePageContentWidth } from 'src/legacy/components/Signature/SignaturePageContent';
import { SignaturePageComponent } from 'src/store/signaturePage/types';
import { NonHoverBorder } from 'src/theme/colors';

type Props = {
  clientSignaturePanelWidth: number;
};

const useStyles = makeStyles((theme: CustomTheme) => ({
  desktopLine: {
    position: 'absolute',
    height: 2,
    backgroundImage: `linear-gradient(to right, ${NonHoverBorder} 2px, rgba(255, 255, 255, 0) 0%)`,
    backgroundPosition: 'top',
    backgroundSize: '6px 2px', // background image 6px wide and 2px tall
    backgroundRepeat: 'repeat-x',
    zIndex: theme.zIndex.contractSignatureComponent - 1, // Ensure that the connecting line is always positioned behind the signature components
    maskImage: 'linear-gradient(to right, transparent 0%, black 15%)', // fade out because it doesn't line up perfectly across browsers
    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 15%)',
  },
  mobileLine: {
    position: 'absolute',
    width: '2px',
    height: '100%',
    backgroundImage: `linear-gradient(${NonHoverBorder} 2px, rgba(255, 255, 255, 0) 0%)`,
    backgroundPosition: 'right',
    backgroundSize: '2px 6px', // background image 2px wide and 6px tall
    backgroundRepeat: 'repeat-y',
    zIndex: theme.zIndex.contractSignatureComponent - 1, // Ensure that the connecting line is always positioned behind the signature components
  },
}));

const SignatureConnectingLine = ({ clientSignaturePanelWidth }: Props) => {
  const classes = useStyles();
  const [activeComponent, setActiveComponent] = React.useState<
    SignaturePageComponent | undefined
  >(undefined);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { activeComponentKey, pageComponents } = useAppSelector(
    ({ signaturePage }) => ({
      activeComponentKey: signaturePage.activeComponentKey,
      pageComponents: signaturePage.pageComponents,
    }),
  );

  React.useEffect(() => {
    // find active component using active key
    const component = pageComponents?.find((c) => c.key === activeComponentKey);

    // If component width and height is not available fallback to default width and height
    if (component) {
      const stagedComponent = { ...component };
      if (!stagedComponent.width) {
        stagedComponent.width = SIGNATURE_COMPONENT_DEFAULT_WIDTH;
      }

      if (!stagedComponent?.height) {
        stagedComponent.height = SIGNATURE_COMPONENT_DEFAULT_HEIGHT;
      }
      setActiveComponent(stagedComponent);
      return;
    }

    setActiveComponent(component);
  }, [activeComponentKey, pageComponents]);

  // Calculate the left margin of the PDF content within the PDF area Grid column.
  // The PDF area width = viewport - sidebar width, and the PDF content (680px) is centered within it.
  const pdfLeftMargin = useMemo(() => {
    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    const pdfAreaWidth =
      window.innerWidth - scrollbarWidth - clientSignaturePanelWidth;
    return Math.max(0, (pdfAreaWidth - signaturePageContentWidth) / 2);
  }, [clientSignaturePanelWidth]);

  // Position line to extend from the active component to the sidebar divider.
  // Subtract 1px to stop exactly at the divider border (not past it).
  const desktopLeft = -(pdfLeftMargin - 1);
  const desktopWidth = pdfLeftMargin - 1 + (activeComponent?.xPosition ?? 0);

  if (!activeComponent) return null;

  if (isSmallScreen) {
    return (
      <div
        data-testid="signature-connect-line"
        className={classes.mobileLine}
        style={{
          left: activeComponent.xPosition + activeComponent.width / 2,
          top: activeComponent.yPosition + activeComponent.height,
        }}
      />
    );
  }

  return (
    <div
      data-testid="signature-connect-line"
      className={classes.desktopLine}
      style={{
        left: desktopLeft,
        top: activeComponent.yPosition + activeComponent.height / 2,
        width: desktopWidth,
      }}
    />
  );
};

export default SignatureConnectingLine;
